import React from 'react';
import HelpArticleLayout, { StatusChip, UiLabel, InfoCallout } from '../components/HelpArticleLayout';

// Help article: "How do I upload a compliance document?" — sections template.

const ICON = {
  check:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></svg>,
  upload: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15V4" /><polyline points="7 9 12 4 17 9" /><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /></svg>,
  clock:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
  refresh:<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 15-6.7L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" /><path d="M3 21v-5h5" /></svg>,
  help:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><path d="M12 17h.01" /></svg>,
};

const sections = [
  {
    id: 'before', title: 'Before you start', icon: ICON.check,
    body: (
      <>
        <p>Have the file ready on your device. Most documents are PDFs, but JPGs and PNGs of certificates work too. Files need to be under 10 MB — if yours is larger, compress it or save the PDF at a lower quality setting first.</p>
        <p>If the document has an expiry date (insurance policies, food hygiene ratings, PAT testing certificates), have that date to hand too. You&rsquo;ll need to enter it during upload.</p>
      </>
    ),
  },
  {
    id: 'steps', title: 'Step by step', icon: ICON.upload,
    body: (
      <>
        <ol className="list-decimal pl-5 space-y-2 marker:text-fusion-forest marker:font-bold">
          <li>From your portal home, find the document under <UiLabel>Compliance</UiLabel>. Each row shows the document name and what&rsquo;s needed (e.g. minimum cover amounts, required file types).</li>
          <li>Click the green <UiLabel>Upload</UiLabel> button on the right of the row.</li>
          <li>Choose your file from the file picker. PDF, JPG, PNG, or DOCX are all accepted.</li>
          <li>If the document expires, enter the expiry date. If it doesn&rsquo;t expire, leave the date blank.</li>
          <li>Click <UiLabel>Submit</UiLabel>. You&rsquo;ll see the row change from <StatusChip tone="outstanding">Outstanding</StatusChip> to <StatusChip tone="review">Awaiting Review</StatusChip>.</li>
        </ol>
        <InfoCallout><strong className="text-ink">You don&rsquo;t have to upload everything in one sitting.</strong> Documents save as you go. Come back later and continue where you left off.</InfoCallout>
      </>
    ),
  },
  {
    id: 'next', title: 'What happens next', icon: ICON.clock,
    body: (
      <>
        <p>The Fusion team reviews uploads within two working days. You&rsquo;ll see one of three states on the row:</p>
        <ul className="list-disc pl-5 space-y-2 marker:text-fusion-forest">
          <li><StatusChip tone="review">Awaiting Review</StatusChip> — Fusion 2K has your file and is checking it</li>
          <li><StatusChip tone="approved">Approved</StatusChip> — all good, nothing more to do</li>
          <li><StatusChip tone="outstanding">Outstanding</StatusChip> — something needs attention. Fusion 2K will email you with what&rsquo;s needed</li>
        </ul>
        <p>Once approved, expiring documents will flip to <StatusChip tone="expiring">Expiring Soon</StatusChip> in the 30 days before the expiry date. You&rsquo;ll get an email reminder before that happens.</p>
      </>
    ),
  },
  {
    id: 'replace', title: 'Replacing an existing document', icon: ICON.refresh,
    body: (
      <p>If your insurance has been renewed, or you&rsquo;ve got an updated H&amp;S policy, click <UiLabel>View / Replace</UiLabel> on the approved row. Uploading a new file supersedes the old one — Fusion 2K keeps both on record but treats your latest upload as the current version.</p>
    ),
  },
  {
    id: 'issues', title: 'Common issues', icon: ICON.help,
    body: (
      <>
        <p><strong className="text-ink">File too large.</strong> Most PDFs can be compressed by saving them with &ldquo;reduce file size&rdquo; or running them through a free online PDF compressor.</p>
        <p><strong className="text-ink">Wrong file type.</strong> If you&rsquo;ve got a Word document, save or export it as a PDF first.</p>
        <p><strong className="text-ink">Photo of a certificate.</strong> Fine, but make sure the whole document is in frame, in focus, and well lit. A JPG of a clearly readable certificate is better than a poor scan.</p>
      </>
    ),
  },
];

export default function HelpUploadCompliance() {
  return (
    <HelpArticleLayout
      breadcrumb="Compliance documents"
      title="How do I upload a compliance document?"
      intro="Uploading a compliance document takes about a minute per file. Here&rsquo;s the full walkthrough — and what happens after you hit upload."
      readTime="3 min read"
      sections={sections}
      footerText="If you're stuck or have a document we haven't asked about, just drop us a line."
    />
  );
}
